"use client"

import { useMemo, useState } from "react"
import { Check, CheckCircle2, XCircle } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/common/components/ui/button"
import { Progress } from "@/common/components/ui/progress"
import { cn } from "@/common/lib/utils"
import { BAUSTEIN_BY_ID } from "@/modules/lernpfad/data/bausteine"
import { instrumentFor } from "@/modules/lernpfad/data/evidence-seed"
import { SKILL_BY_ID } from "@/modules/lernpfad/data/skills"
import type { LearningEvidence } from "@/modules/lernpfad/engine"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import { generateQuiz } from "./quiz-generator"

/**
 * The course player: slides → knowledge check → reaction survey → result.
 *
 * This is the point of building a real LMS surface rather than reusing the old
 * one-click "mark done" dialog. Finishing here produces genuine
 * `LearningEvidence` — a scored result against a real instrument, and an L1
 * reaction — through the exact same shape the seeded population carries
 * (`data/evidence-seed.ts`). A knowledge-check pass recorded here is subject to
 * whatever evidence floor is currently configured on the Steuerung page: under
 * Kirkpatrick's default it clears the bar; raise the floor to LTEM's
 * `entscheidungstest` and the SAME pass no longer counts as competence. That
 * gap is not a bug to paper over — it is the demonstration.
 *
 * The quiz outcome never blocks completion. A real LMS records the attempt
 * either way; whether it counts as evidence is the ENGINE's judgement, made
 * from the stored `scaled` value at read time (`evidence.ts`'s
 * `provenInstrument`), not a gate this dialog enforces.
 */

type Phase = "slides" | "quiz" | "reaction" | "result"

const PASS_THRESHOLD = 0.66 // the documented default — see policy.ts DEFAULT_POLICY.evidence.learningPassScaled

export default function LmsPlayer({
    state,
    bausteinId,
    onDone,
}: {
    state: LernpfadDemoState
    bausteinId: string
    onDone: () => void
}) {
    const { localizer, recordLmsEvidence, asOf } = state
    const { t } = localizer
    const baustein = BAUSTEIN_BY_ID.get(bausteinId)

    const quiz = useMemo(() => (baustein ? generateQuiz(baustein) : []), [baustein])
    const instrument = baustein ? instrumentFor(baustein) : null

    const [phase, setPhase] = useState<Phase>("slides")
    const [slideIndex, setSlideIndex] = useState(0)
    const [quizIndex, setQuizIndex] = useState(0)
    const [answers, setAnswers] = useState<number[]>([])
    const [picked, setPicked] = useState<number | null>(null)
    const [reaction, setReaction] = useState<{ relevance: number; apply: number }>({
        relevance: 0,
        apply: 0,
    })

    if (!baustein) return null
    const content = localizer.baustein(baustein)

    const sentences = content.description.split(/(?<=[.:])\s+/).filter(Boolean)
    const slides = [
        sentences.slice(0, Math.ceil(sentences.length / 2)).join(" "),
        sentences.slice(Math.ceil(sentences.length / 2)).join(" ") || content.description,
        content.title,
    ]
    const isLastSlide = slideIndex >= slides.length - 1

    const correctCount = answers.filter((answer, index) => answer === quiz[index]?.correctIndex).length
    const scaled = quiz.length > 0 ? Math.round((correctCount / quiz.length) * 100) / 100 : 0
    const passed = scaled >= PASS_THRESHOLD

    const finish = () => {
        const evidence: LearningEvidence = { bausteinId }
        if (reaction.relevance > 0 && reaction.apply > 0) {
            evidence.reaction = { relevance: reaction.relevance, likelihoodToApply: reaction.apply, on: asOf }
        }
        if (instrument && quiz.length > 0) {
            evidence.learning = { instrument, scaled, passed, on: asOf }
        }
        recordLmsEvidence(evidence)
        toast.success(t("toast.ags"), { description: t("toast.agsNote"), duration: 6000 })
        onDone()
    }

    const selectOption = (index: number) => setPicked(index)

    const advanceQuiz = () => {
        if (picked === null) return
        const next = [...answers]
        next[quizIndex] = picked
        setAnswers(next)
        setPicked(null)
        if (quizIndex < quiz.length - 1) {
            setQuizIndex(quizIndex + 1)
            return
        }
        setPhase("reaction")
    }

    return (
        <div className="p-6">
            <h3 className="text-lg font-semibold">{content.title}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{content.provider}</p>

            <div className="mt-4">
                <Progress value={phaseProgress(phase, slideIndex, slides.length, quizIndex, quiz.length)} />
            </div>

            {phase === "slides" && (
                <div className="mt-4 space-y-4">
                    <p className="text-xs text-muted-foreground">
                        {t("player.slide", { current: slideIndex + 1, total: slides.length })}
                    </p>
                    <div className="min-h-[140px] rounded-lg border bg-muted/20 p-5 text-sm leading-relaxed">
                        {slides[slideIndex]}
                    </div>
                    <div className="flex justify-end">
                        {isLastSlide ? (
                            <Button
                                onClick={() =>
                                    setPhase(instrument && quiz.length > 0 ? "quiz" : "reaction")
                                }
                            >
                                {t("player.finish")}
                            </Button>
                        ) : (
                            <Button variant="outline" onClick={() => setSlideIndex((i) => i + 1)}>
                                {t("player.nextSlide")}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {phase === "quiz" && quiz[quizIndex] && (
                <div className="mt-4 space-y-4">
                    <p className="text-xs text-muted-foreground">
                        {t("lms.player.question", { current: quizIndex + 1, total: quiz.length })}
                    </p>
                    <p className="text-sm font-medium leading-relaxed">
                        {t(quiz[quizIndex].stemKey, {
                            skill: localizer.skill(
                                quiz[quizIndex].skillId,
                                SKILL_BY_ID.get(quiz[quizIndex].skillId)?.label ?? quiz[quizIndex].skillId,
                            ),
                        })}
                    </p>
                    <div className="space-y-2">
                        {quiz[quizIndex].optionKeys.map((optionKey, index) => (
                            <button
                                key={optionKey}
                                type="button"
                                onClick={() => selectOption(index)}
                                className={cn(
                                    "flex w-full items-start gap-2 rounded-lg border p-3 text-left text-sm transition-colors",
                                    picked === index
                                        ? "border-primary bg-primary/5"
                                        : "hover:bg-muted/40",
                                )}
                            >
                                <span
                                    className={cn(
                                        "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
                                        picked === index && "border-primary bg-primary text-primary-foreground",
                                    )}
                                >
                                    {picked === index && <Check className="size-3" />}
                                </span>
                                {t(optionKey)}
                            </button>
                        ))}
                    </div>
                    <div className="flex justify-end">
                        <Button disabled={picked === null} onClick={advanceQuiz}>
                            {quizIndex < quiz.length - 1 ? t("player.nextSlide") : t("lms.player.showResult")}
                        </Button>
                    </div>
                </div>
            )}

            {phase === "reaction" && (
                <div className="mt-4 space-y-5">
                    <p className="text-xs text-muted-foreground">{t("lms.player.reactionIntro")}</p>
                    <RatingRow
                        label={t("lms.player.reaction.relevance")}
                        value={reaction.relevance}
                        onChange={(value) => setReaction((r) => ({ ...r, relevance: value }))}
                        lowLabel={t("lms.player.scaleLow")}
                        highLabel={t("lms.player.scaleHigh")}
                    />
                    <RatingRow
                        label={t("lms.player.reaction.apply")}
                        value={reaction.apply}
                        onChange={(value) => setReaction((r) => ({ ...r, apply: value }))}
                        lowLabel={t("lms.player.scaleLow")}
                        highLabel={t("lms.player.scaleHigh")}
                    />
                    <div className="flex justify-end">
                        <Button
                            disabled={reaction.relevance === 0 || reaction.apply === 0}
                            onClick={() => setPhase("result")}
                        >
                            {t("lms.player.showResult")}
                        </Button>
                    </div>
                </div>
            )}

            {phase === "result" && (
                <div className="mt-4 space-y-4">
                    <div className="flex flex-col items-center gap-2 rounded-lg border bg-muted/20 p-6 text-center">
                        {quiz.length > 0 ? (
                            <>
                                {passed ? (
                                    <CheckCircle2 className="size-8 text-emerald-600 dark:text-emerald-400" />
                                ) : (
                                    <XCircle className="size-8 text-muted-foreground" />
                                )}
                                <p className="text-base font-semibold">
                                    {passed ? t("player.passed") : t("lms.player.result.title")}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    {t("lms.player.result.score", {
                                        correct: correctCount,
                                        total: quiz.length,
                                        percent: Math.round(scaled * 100),
                                    })}
                                </p>
                                {!passed && (
                                    <p className="max-w-sm text-xs text-muted-foreground">
                                        {t("lms.player.result.failed")}
                                    </p>
                                )}
                            </>
                        ) : (
                            <>
                                <CheckCircle2 className="size-8 text-emerald-600 dark:text-emerald-400" />
                                <p className="text-base font-semibold">{t("player.passed")}</p>
                            </>
                        )}
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={finish}>{t("player.close")}</Button>
                    </div>
                </div>
            )}
        </div>
    )
}

function phaseProgress(
    phase: Phase,
    slideIndex: number,
    slideCount: number,
    quizIndex: number,
    quizCount: number,
): number {
    // Four rough bands rather than one precise fraction — a course player's
    // progress bar communicates "roughly where am I", not a precise percentage.
    if (phase === "slides") return ((slideIndex + 1) / slideCount) * 40
    if (phase === "quiz") return 40 + ((quizIndex + 1) / Math.max(1, quizCount)) * 35
    if (phase === "reaction") return 85
    return 100
}

function RatingRow({
    label,
    value,
    onChange,
    lowLabel,
    highLabel,
}: {
    label: string
    value: number
    onChange: (value: number) => void
    lowLabel: string
    highLabel: string
}) {
    return (
        <div className="space-y-1.5">
            <p className="text-sm">{label}</p>
            <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{lowLabel}</span>
                <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button
                            key={n}
                            type="button"
                            onClick={() => onChange(n)}
                            aria-pressed={value === n}
                            className={cn(
                                "flex size-8 items-center justify-center rounded-full border text-sm font-medium transition-colors",
                                value === n
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : "text-muted-foreground hover:bg-muted/40",
                            )}
                        >
                            {n}
                        </button>
                    ))}
                </div>
                <span className="text-xs text-muted-foreground">{highLabel}</span>
            </div>
        </div>
    )
}
